import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';

const EMPTY = {
  position: '1',
  name: '',
  short_code: '',
  round_result: false,
  show_in_dialogs: true,
  count_in_percent_honorarium: false,
  manual_offset_account: false,
  value_list: '',
  monthly_col_nr: '',
  monthly_col_label: '',
  single_col_nr: '',
  single_col_label: '',
  cost_center: '',
  partial_service: '',
  revenue_account: '',
  is_active: true,
};

const MONTHLY_COL_OPTIONS = [
  { n: 1, label: 'Zuschlag' },
  { n: 2, label: 'Gebühr' },
  { n: 3, label: 'GEMA' },
  { n: 4, label: 'Ermäßigung' },
  { n: 5, label: 'Sozialfonds' },
  { n: 6, label: 'Mahnung' },
  { n: 7, label: 'Sonstiges' },
  { n: 8, label: 'Spalte 8' },
];
const SINGLE_COL_OPTIONS = [
  { n: 1, label: 'Zuschlag' },
  { n: 2, label: 'Gebühr' },
  { n: 3, label: 'GEMA' },
  { n: 4, label: 'Ermäßigung' },
  { n: 5, label: 'Sonstiges' },
];

const NONE = '_none';

function colValue(n) {
  return n == null || n === '' ? NONE : String(n);
}

function ColSelect({ value, options, onChange, testId, className }) {
  return (
    <Select
      value={colValue(value)}
      onValueChange={(v) => {
        if (v === NONE) {
          onChange(null, null);
          return;
        }
        const n = parseInt(v, 10);
        const opt = options.find((o) => o.n === n);
        onChange(n, opt?.label || null);
      }}
    >
      <SelectTrigger
        className={`h-8 px-2 text-xs bg-white ${className || ''}`}
        data-testid={testId}
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent className="z-[200]">
        <SelectItem value={NONE}>—</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.n} value={String(o.n)}>
            {o.n} · {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function toForm(row) {
  return {
    ...row,
    position: row.position != null ? String(row.position) : '0',
    short_code: row.short_code || '',
    value_list: row.value_list || '',
    monthly_col_nr: row.monthly_col_nr != null ? String(row.monthly_col_nr) : '',
    monthly_col_label: row.monthly_col_label || '',
    single_col_nr: row.single_col_nr != null ? String(row.single_col_nr) : '',
    single_col_label: row.single_col_label || '',
    cost_center: row.cost_center || '',
    partial_service: row.partial_service || '',
    revenue_account: row.revenue_account || '',
  };
}

function toPayload(f) {
  const numOrNull = (v) => (v === '' || v == null ? null : parseInt(v, 10));
  return {
    position: parseInt(f.position, 10) || 0,
    name: (f.name || '').trim(),
    short_code: f.short_code?.trim() || null,
    round_result: !!f.round_result,
    show_in_dialogs: !!f.show_in_dialogs,
    count_in_percent_honorarium: !!f.count_in_percent_honorarium,
    manual_offset_account: !!f.manual_offset_account,
    value_list: f.value_list?.trim() || null,
    monthly_col_nr: numOrNull(f.monthly_col_nr),
    monthly_col_label: f.monthly_col_label?.trim() || null,
    single_col_nr: numOrNull(f.single_col_nr),
    single_col_label: f.single_col_label?.trim() || null,
    cost_center: f.cost_center?.trim() || null,
    partial_service: f.partial_service?.trim() || null,
    revenue_account: f.revenue_account?.trim() || null,
    is_active: !!f.is_active,
  };
}

export default function DiscountCategoriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/tariffs-discounts/discount-categories');
      setRows(r.data || []);
    } catch {
      toast.error('Kategorien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { getRowProps } = useListKeyboardNav({
    items: rows,
    onSelect: (row) => setEditor(toForm(row)),
    enabled: !editor && !toDelete,
  });

  const save = async () => {
    const payload = toPayload(editor);
    if (!payload.name) { toast.error('Name ist erforderlich'); return; }
    try {
      if (editor.id) {
        await api.patch(`/tariffs-discounts/discount-categories/${editor.id}`, payload);
        toast.success('Kategorie aktualisiert');
      } else {
        await api.post('/tariffs-discounts/discount-categories', payload);
        toast.success('Kategorie angelegt');
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/tariffs-discounts/discount-categories/${toDelete.id}`);
      toast.success('Kategorie gelöscht');
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const patchCol = async (row, patch) => {
    try {
      const r = await api.patch(`/tariffs-discounts/discount-categories/${row.id}`, patch);
      setRows((prev) => prev.map((x) => (x.id === row.id ? r.data : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Spalte konnte nicht gespeichert werden');
      load();
    }
  };

  return (
    <div className="space-y-3" data-testid="discount-categories-tab">
      <p className="text-sm text-slate-600">
        Kategorien für Rabatte/Zuschläge auf Rechnungen. Mo-/Ez-Spalte direkt in der Tabelle wählbar.
      </p>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Rabatte / Zuschläge</h2>
          <Button size="sm" variant="outline" onClick={() => setEditor({ ...EMPTY })} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Kategorie
          </Button>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-red" />
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 font-semibold">Pos.</th>
                  <th className="px-3 py-2 font-semibold">Kategorie</th>
                  <th className="px-3 py-2 font-semibold">Kurzform</th>
                  <th className="px-3 py-2 font-semibold text-center">Runden</th>
                  <th className="px-3 py-2 font-semibold text-center">Dialoge</th>
                  <th className="px-3 py-2 font-semibold text-center">%-Honorar</th>
                  <th className="px-3 py-2 font-semibold min-w-[150px]">Mo-Spalte</th>
                  <th className="px-3 py-2 font-semibold min-w-[150px]">Ez-Spalte</th>
                  <th className="px-3 py-2 font-semibold w-20" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-400">Noch keine Kategorien</td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={LIST_ROW_CLASS}
                    data-testid={`category-row-${row.id}`}
                    {...getRowProps(idx, row)}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.position}</td>
                    <td className="px-3 py-2 font-medium">
                      {row.name}
                      {!row.is_active && <span className="ml-1.5 text-[10px] text-slate-400">inaktiv</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{row.short_code || ''}</td>
                    <td className="px-3 py-2 text-center">{row.round_result ? '✓' : ''}</td>
                    <td className="px-3 py-2 text-center">{row.show_in_dialogs ? '✓' : ''}</td>
                    <td className="px-3 py-2 text-center">{row.count_in_percent_honorarium ? '✓' : ''}</td>
                    <td className="px-2 py-1.5">
                      <ColSelect
                        value={row.monthly_col_nr}
                        options={MONTHLY_COL_OPTIONS}
                        testId={`monthly-col-${row.id}`}
                        onChange={(n, label) => patchCol(row, {
                          monthly_col_nr: n,
                          monthly_col_label: n == null ? null : label,
                        })}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <ColSelect
                        value={row.single_col_nr}
                        options={SINGLE_COL_OPTIONS}
                        testId={`single-col-${row.id}`}
                        onChange={(n, label) => patchCol(row, {
                          single_col_nr: n,
                          single_col_label: n == null ? null : label,
                        })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => setToDelete(row)} title="Löschen">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3 mt-1">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Position</Label>
                  <Input type="number" value={editor.position} onChange={(e) => setEditor({ ...editor, position: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="category-name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kurzform</Label>
                  <Input value={editor.short_code} onChange={(e) => setEditor({ ...editor, short_code: e.target.value })} />
                </div>
                <div>
                  <Label>Werteliste</Label>
                  <Input value={editor.value_list} onChange={(e) => setEditor({ ...editor, value_list: e.target.value })} placeholder="z.B. ja" />
                </div>
              </div>
              <div className="flex flex-wrap gap-5 py-1">
                {[
                  ['round_result', 'Ergebnis runden'],
                  ['show_in_dialogs', 'In Rabatt-Dialogen'],
                  ['count_in_percent_honorarium', 'In %-Honorar'],
                  ['manual_offset_account', 'Ausgleichskonto manuell'],
                  ['is_active', 'Aktiv'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Switch checked={!!editor[key]} onCheckedChange={(v) => setEditor({ ...editor, [key]: v })} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2 p-3 rounded-md bg-slate-50">
                  <p className="text-xs font-semibold text-slate-700">Rechnung Monatssumme (Mo-Spalte)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Spalte Nr.</Label>
                      <ColSelect
                        value={editor.monthly_col_nr}
                        options={MONTHLY_COL_OPTIONS}
                        testId="editor-monthly-col"
                        className="h-10"
                        onChange={(n, label) => setEditor({
                          ...editor,
                          monthly_col_nr: n == null ? '' : String(n),
                          monthly_col_label: n == null ? '' : (label || editor.monthly_col_label),
                        })}
                      />
                    </div>
                    <div>
                      <Label>Label</Label>
                      <Input value={editor.monthly_col_label} onChange={(e) => setEditor({ ...editor, monthly_col_label: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2 p-3 rounded-md bg-slate-50">
                  <p className="text-xs font-semibold text-slate-700">Rechnung Einzelsumme (Ez-Spalte)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Spalte Nr.</Label>
                      <ColSelect
                        value={editor.single_col_nr}
                        options={SINGLE_COL_OPTIONS}
                        testId="editor-single-col"
                        className="h-10"
                        onChange={(n, label) => setEditor({
                          ...editor,
                          single_col_nr: n == null ? '' : String(n),
                          single_col_label: n == null ? '' : (label || editor.single_col_label),
                        })}
                      />
                    </div>
                    <div>
                      <Label>Label</Label>
                      <Input value={editor.single_col_label} onChange={(e) => setEditor({ ...editor, single_col_label: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Kostenstelle</Label>
                  <Input value={editor.cost_center} onChange={(e) => setEditor({ ...editor, cost_center: e.target.value })} />
                </div>
                <div>
                  <Label>Teilleistung</Label>
                  <Input value={editor.partial_service} onChange={(e) => setEditor({ ...editor, partial_service: e.target.value })} />
                </div>
                <div>
                  <Label>Einnahmekonto</Label>
                  <Input value={editor.revenue_account} onChange={(e) => setEditor({ ...editor, revenue_account: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Abbrechen</Button>
            <Button onClick={save} data-testid="save-category-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.name}</strong> wird entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
